import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------- brand */

export function Wordmark({ className }: { className?: string }) {
  return <span className={cn("wordmark", className)}>Decore Wordle</span>;
}

/**
 * The three-tile mark. Same colours the board uses, so the logo is a legend.
 */
export function TileMark({ size = 22 }: { size?: number }) {
  const cells: [string, string][] = [
    ["D", "tile-hit"],
    ["W", "tile-near"],
    ["!", "tile-miss"],
  ];
  return (
    <span className="flex items-center gap-1" aria-hidden>
      {cells.map(([letter, tone]) => (
        <span
          key={letter}
          className={cn("tile", tone)}
          style={{ width: size, height: size, fontSize: size * 0.55, borderWidth: 0 }}
        >
          {letter}
        </span>
      ))}
    </span>
  );
}

/* ------------------------------------------------------------------ header */

export function TopBar({
  left,
  right,
  title,
}: {
  left?: ReactNode;
  right?: ReactNode;
  title?: ReactNode;
}) {
  return (
    <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-line px-3 sm:px-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">{left}</div>
      <div className="wordmark shrink-0 text-[18px] sm:text-[22px]">{title ?? "Decore Wordle"}</div>
      <div className="flex min-w-0 flex-1 items-center justify-end gap-2">{right}</div>
    </header>
  );
}

/* ------------------------------------------------------------------ inputs */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost";
};

export function Button({ variant = "primary", className, ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cn("btn", variant === "primary" ? "btn-primary" : "btn-ghost", className)}
    />
  );
}

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string | null;
};

export function Field({ label, hint, error, className, ...props }: FieldProps) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-ink-dim">
        {label}
      </span>
      <input {...props} className={cn("field", className)} />
      {error ? (
        <span className="text-[13px] text-near">{error}</span>
      ) : hint ? (
        <span className="text-[13px] text-ink-faint">{hint}</span>
      ) : null}
    </label>
  );
}

/* ------------------------------------------------------------------ toasts */

/** Wordle's own feedback: a black pill in the middle of the board, briefly. */
export function Toast({ text }: { text: string }) {
  return (
    <div className="pointer-events-none absolute left-1/2 top-4 z-50 -translate-x-1/2">
      <div className="toast-in rounded-[4px] bg-ink px-4 py-3 text-[14px] font-bold text-bg shadow-lg">
        {text}
      </div>
    </div>
  );
}
